/**
 * @file Shared search-state hook for the lightweight player-search surfaces
 * (header search popover, dashboard PlayerSearch card): debounced name search
 * that yields to the Transfermarkt URL lookup when the input parses as a
 * player profile URL (SE-3599).
 * @remarks
 * Extracted from HeaderSearchButtonContainer and PlayerSearchContainer,
 * which previously duplicated this state machine verbatim. The heavier
 * modal surfaces (GlobalSearchModal, search-modal) keep their own
 * usePlayerSearch plumbing — they add sort/filter/pagination this hook
 * deliberately does not model.
 */

import { useCallback, useMemo, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import {
  PlayerSearchPlayerFragment,
  usePlayerSearchSearchPlayersLiteQuery,
} from "@/generated/graphql";
import {
  TransfermarktLookupOptions,
  useTransfermarktPlayerLookup,
} from "@/hooks/useTransfermarktPlayerLookup";
import { isTransfermarktUrl } from "@/utils/transfermarktUrl";

/**
 * Search state shared by the header and dashboard player-search surfaces
 */
interface PlayerNameOrTransfermarktSearchResult {
  /** The raw input text (updates on every keystroke) */
  searchText: string;
  /** Name-search results for the debounced text */
  results: PlayerSearchPlayerFragment[];
  /** Name search or URL lookup in flight */
  isLoading: boolean;
  /** Transfermarkt ID that resolved to no player, for the empty state */
  transfermarktNotFoundId: number | null;
  /** Input change handler — updates the text and its debounced mirror */
  handleChangeText: (text: string) => void;
  /** Clears the input and its debounced mirror */
  resetSearch: () => void;
}

/**
 * Debounced player-name search that yields to the Transfermarkt URL lookup
 * when the input parses as a profile URL. After a URL resolves and
 * navigates, the hook clears its own search state, then invokes the
 * surface's `onNavigated` cleanup (close the popover, drop focus, …).
 * @param options - Breadcrumb origin and surface-specific post-navigation cleanup
 * @returns Search state for the surface's view props
 */
export function usePlayerNameOrTransfermarktSearch(
  options?: TransfermarktLookupOptions
): PlayerNameOrTransfermarktSearchResult {
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");

  const debouncedSetSearch = useDebouncedCallback(setDebouncedSearchText, 300);

  // Lite document: this hook renders a name-suggestion list and never a result
  // count, so it must not pay for totalCount's unbounded server-side COUNT(*)
  // (SE-5776). On dev this hook is the search path for HeaderSearchButton and
  // the dashboard PlayerSearch, which call the query directly on other branches.
  const { data, loading } = usePlayerSearchSearchPlayersLiteQuery({
    variables: {
      query: debouncedSearchText,
      first: 10,
    },
    // Never text-search a Transfermarkt URL — it resolves via the dedicated
    // ID lookup below (SE-3599).
    skip:
      !debouncedSearchText ||
      debouncedSearchText.length < 2 ||
      isTransfermarktUrl(debouncedSearchText),
    fetchPolicy: "cache-and-network",
  });

  const results: PlayerSearchPlayerFragment[] = useMemo(
    () =>
      data?.searchPlayers?.edges
        ?.map(edge => edge?.node)
        .filter((node): node is PlayerSearchPlayerFragment => !!node) ?? [],
    [data?.searchPlayers?.edges]
  );

  const handleChangeText = useCallback(
    (text: string) => {
      setSearchText(text);
      debouncedSetSearch(text);
    },
    [debouncedSetSearch]
  );

  const resetSearch = useCallback(() => {
    // Cancel any queued debounced write first — without this, a pending
    // invocation can restore the old text right after the reset and
    // re-trigger a stale name query.
    debouncedSetSearch.cancel();
    setSearchText("");
    setDebouncedSearchText("");
  }, [debouncedSetSearch]);

  // Pasted Transfermarkt URLs resolve by numeric ID and auto-navigate to the
  // player profile (SE-3599). The lookup reads its options through a ref, so
  // the wrapper's per-render identity is harmless.
  const transfermarktLookup = useTransfermarktPlayerLookup(searchText, {
    origin: options?.origin,
    onNavigated: () => {
      resetSearch();
      options?.onNavigated?.();
    },
  });

  return {
    searchText,
    results,
    isLoading:
      (loading && debouncedSearchText.length >= 2) ||
      transfermarktLookup.isLookingUp,
    transfermarktNotFoundId: transfermarktLookup.isNotFound
      ? transfermarktLookup.transfermarktId
      : null,
    handleChangeText,
    resetSearch,
  };
}
